import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Download, Settings2, Sparkles } from 'lucide-react';
import { useLang } from '../../../context/LangContext';
import { GlassCard } from '../../shared/GlassCard';
import { fadeInUp, SPRING } from '../../../animations/variants';

const steps = [
  {
    num: "01",
    titleKey: "secteurs.process.1.title",
    descKey: "secteurs.process.1.desc",
    pillsKey: "secteurs.process.1.pills",
    icon: Download,
    iconColor: "text-brand-green",
    dotColor: "bg-brand-green",
  },
  {
    num: "02",
    titleKey: "secteurs.process.2.title",
    descKey: "secteurs.process.2.desc",
    pillsKey: "secteurs.process.2.pills",
    icon: Settings2,
    iconColor: "text-brand-blue",
    dotColor: "bg-brand-blue",
  },
  {
    num: "03",
    titleKey: "secteurs.process.3.title",
    descKey: "secteurs.process.3.desc",
    badgeKey: "secteurs.process.3.badge",
    icon: Sparkles,
    iconColor: "text-brand-gold",
    dotColor: "bg-brand-gold",
    glow: true,
  },
];

export const AssimilationProcess = () => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start center", "end center"] });
  const lineWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section data-testid="assimilation-section" className="relative py-24 md:py-32 bg-[#020209]" ref={containerRef}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div {...fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">
            {t("secteurs.process.title")}
          </h2>
          <p className="text-neutral-400 text-lg max-w-xl mx-auto">{t("secteurs.process.subtitle")}</p>
        </motion.div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden md:block relative">
          <div className="absolute top-8 left-0 right-0 h-px bg-zinc-800" />
          <motion.div className="absolute top-8 left-0 h-px bg-gradient-to-r from-brand-green to-brand-gold origin-left" style={{ width: lineWidth }} />

          <div className="grid grid-cols-3 gap-8 relative">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...SPRING, delay: i * 0.2 }}
                >
                  {/* Dot */}
                  <div className="flex justify-center mb-8">
                    <div className="relative">
                      <div className={`w-4 h-4 rounded-full ${step.dotColor} ${step.glow ? 'shadow-[0_0_12px_rgba(34,211,238,0.5)]' : ''}`} />
                      {step.glow && (
                        <motion.div
                          className="absolute -inset-2 rounded-full border border-brand-gold/30"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                    </div>
                  </div>

                  <GlassCard className="p-6" hover={true}>
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={20} className={step.iconColor} />
                      <span className="text-xs font-mono text-neutral-600">{step.num}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{t(step.titleKey)}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed mb-3">{t(step.descKey)}</p>
                    {step.pillsKey && (
                      <div className="flex flex-wrap gap-2">
                        {t(step.pillsKey).split(",").map((pill, j) => (
                          <span key={j} className="text-xs px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-neutral-500">{pill}</span>
                        ))}
                      </div>
                    )}
                    {step.badgeKey && (
                      <span className="inline-flex text-xs px-3 py-1 rounded-full bg-brand-gold/10 border border-brand-gold/30 text-brand-gold">
                        {t(step.badgeKey)}
                      </span>
                    )}
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden space-y-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <GlassCard className="p-5" hover={true}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-3 h-3 rounded-full ${step.dotColor}`} />
                    <Icon size={18} className={step.iconColor} />
                    <span className="text-xs font-mono text-neutral-600">{step.num}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{t(step.titleKey)}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-3">{t(step.descKey)}</p>
                  {step.pillsKey && (
                    <div className="flex flex-wrap gap-2">
                      {t(step.pillsKey).split(",").map((pill, j) => (
                        <span key={j} className="text-xs px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-neutral-500">{pill}</span>
                      ))}
                    </div>
                  )}
                  {step.badgeKey && (
                    <span className="inline-flex text-xs px-3 py-1 rounded-full bg-brand-gold/10 border border-brand-gold/30 text-brand-gold">
                      {t(step.badgeKey)}
                    </span>
                  )}
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
